// =============================================================================
// BREVITY BUDGET SEED — bootstrap rows for the brevity_budgets pond.
//
// au_standard is the CALIBRATED source of truth: the EXACT numbers the hardcoded
// BUDGETS table carried (byte-for-byte — the golden test locks this). us_standard
// and gb_standard ship as 'inherited' mirrors of au_standard until variant-specific
// calibration lands; a recalibration is a row UPDATE (upsert), never a code change.
//
// This is DATA (a .seed.ts, like public_events.seed.ts) — used to populate the pond
// AND as the in-memory cold-start fallback the BrevityBudgetService leans on when
// the store is unreachable. The pond (DB) is the runtime source of truth; this seed
// is bootstrap data, not algorithm.
// =============================================================================

export type FormatVariant = 'au_standard' | 'us_standard' | 'gb_standard';

/** au_standard is the default — today's behaviour, the golden baseline. */
export const DEFAULT_FORMAT_VARIANT: FormatVariant = 'au_standard';
export const FORMAT_VARIANTS: FormatVariant[] = ['au_standard', 'us_standard', 'gb_standard'];

export interface BrevityBudgetSeedRow {
  formatVariant: FormatVariant;
  /** Mirrors BrevityService.BlockKind. */
  blockKind: string;
  words: number;
  gradeMax: number;
  verifyState: 'calibrated' | 'inherited' | 'seed';
  notes: string;
}

// au_standard — the calibrated ceilings (grade note: 2026-05-27). BYTE-FOR-BYTE the
// previous hardcoded BUDGETS. Do NOT change a number here without re-running the
// brevity golden test (apps/api/services/BrevityService.golden.test.ts).
const AU_STANDARD: { blockKind: string; words: number; gradeMax: number; notes: string }[] = [
  { blockKind: 'cv_bullet', words: 22, gradeMax: 11, notes: 'CV bullet — tight, scannable.' },
  { blockKind: 'cv_summary', words: 60, gradeMax: 11, notes: 'Career profile, not an objective.' },
  { blockKind: 'cv_role_descriptor', words: 40, gradeMax: 11, notes: 'One-line role context.' },
  {
    blockKind: 'cover_letter_paragraph',
    words: 80,
    gradeMax: 12,
    notes: 'Senior-technical density ceiling (12, not 9 — unavoidable vocab).',
  },
  {
    blockKind: 'cover_letter_full',
    words: 400,
    gradeMax: 12,
    notes: 'AU cover letter 250–400 words.',
  },
  {
    blockKind: 'outreach_email',
    words: 150,
    gradeMax: 9,
    notes: 'Cold email — should skim simple.',
  },
  { blockKind: 'profile_headline', words: 18, gradeMax: 11, notes: 'LinkedIn/Seek headline.' },
  { blockKind: 'profile_about', words: 200, gradeMax: 10, notes: 'Profile about section.' },
  {
    blockKind: 'job_fit_reasoning',
    words: 80,
    gradeMax: 12,
    notes:
      'D15 (2026-07-08) — job.reasoning, the "Why this fit" panel body. Same ceiling as cover_letter_paragraph (2-3 sentences of scoring commentary is the same shape as one letter paragraph).',
  },
  {
    blockKind: 'gap_translation',
    words: 60,
    gradeMax: 12,
    notes:
      'D15 (2026-07-08) — gapTranslations[].translation, the "How I framed the gaps" per-gap body. One honest sentence or two, tighter than a full paragraph.',
  },
];

function variantRows(
  variant: FormatVariant,
  verifyState: BrevityBudgetSeedRow['verifyState'],
  noteSuffix = '',
): BrevityBudgetSeedRow[] {
  return AU_STANDARD.map((r) => ({
    formatVariant: variant,
    blockKind: r.blockKind,
    words: r.words,
    gradeMax: r.gradeMax,
    verifyState,
    notes: r.notes + noteSuffix,
  }));
}

/** Every supported variant × every block kind. au_standard is calibrated; us/gb
 *  inherit it (clearly flagged) until their own calibration replaces the rows. */
export const BREVITY_BUDGET_SEED: BrevityBudgetSeedRow[] = [
  ...variantRows('au_standard', 'calibrated'),
  ...variantRows(
    'us_standard',
    'inherited',
    ' (inherited from au_standard pending en-US calibration)',
  ),
  ...variantRows(
    'gb_standard',
    'inherited',
    ' (inherited from au_standard pending en-GB calibration)',
  ),
];
